import React, { type ReactNode, useMemo } from 'react';
import ParentWidget, { WidgetRenderProps } from './ParentWidget';
import type { ChartSize } from './shared/barGraphUtils';
import type { PcrPieChartData, PcrPieSlice } from './PcrPieChartWidget';

export interface PcrStackedBarWidgetProps {
  id: string;
  title: string;
  size?: ChartSize;
  /** Same shape as the PCR pie: one slice per bar, its subSlices are the stacked segments. */
  data?: PcrPieChartData;
  redirect?: { path: string; queryParams?: Record<string, string | number | boolean> };
  metadata?: { loading?: boolean; error?: Error | null; lastUpdated?: Date | null };
  onRefresh?: () => void;
  requiredPermission: string;
  headerExtra?: ReactNode;
  /** Label for the single bar used when slices have no subSlices (e.g. response-time buckets). */
  singleBarLabel?: string;
  /** Shown instead of empty bars when there is nothing to chart (e.g. no PCRs that month). */
  emptyMessage?: string;
}

interface Bar {
  label: string;
  total: number;
  segments: PcrPieSlice[];
}

/** One bar per slice (segments = subSlices), or a single bar of all slices when none have subSlices. */
export function pcrStackedBars(data: PcrPieChartData | undefined, singleBarLabel = 'All'): Bar[] {
  const slices = data?.slices ?? [];
  if (slices.length === 0) return [];
  if (slices.some((slice) => slice.subSlices?.length)) {
    return slices.map((slice) => ({
      label: slice.label,
      total: slice.count,
      segments: slice.subSlices ?? [slice]
    }));
  }
  return [{ label: singleBarLabel, total: slices.reduce((sum, s) => sum + s.count, 0), segments: slices }];
}

/**
 * Compact stacked bars for the home PCR strip: a row per service area, split by status, scaled to
 * the busiest area so rows compare at a glance. Replaces the PCR pie on the home page.
 */
export function PcrStackedBarWidget({
  id,
  title,
  size = 'medium',
  data,
  redirect,
  metadata,
  onRefresh,
  requiredPermission,
  headerExtra,
  singleBarLabel = 'All',
  emptyMessage
}: PcrStackedBarWidgetProps) {
  const isEmpty = useMemo(
    () => (d: PcrPieChartData | undefined) => (d?.slices?.reduce((s, x) => s + x.count, 0) ?? 0) === 0,
    []
  );

  const renderBars = ({ data: chartData, metadata: chartMetadata }: WidgetRenderProps<PcrPieChartData>) => {
    if (emptyMessage && chartMetadata.isEmpty) {
      return <p className="tw-mb-0 tw-px-2 tw-text-center tw-text-xs tw-text-content-muted">{emptyMessage}</p>;
    }
    const bars = pcrStackedBars(chartData, singleBarLabel);
    const max = Math.max(1, ...bars.map((bar) => bar.total));
    // Legend from the first bar's segments (every bar shares the same segment set)
    const legend = bars[0]?.segments ?? [];

    return (
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-1.5 pcr-stacked-bars">
        {bars.map((bar) => (
          <div key={bar.label} className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
            <span className="tw-w-28 tw-shrink-0 tw-truncate tw-text-content" title={bar.label}>
              {bar.label}
            </span>
            <div className="tw-relative tw-h-3.5 tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-rounded-sm tw-bg-surface-alt">
              <div className="tw-flex tw-h-full" style={{ width: `${(bar.total / max) * 100}%` }}>
                {bar.segments.map((segment) =>
                  segment.count > 0 ? (
                    <div
                      key={segment.label}
                      className="tw-h-full"
                      style={{
                        width: `${bar.total > 0 ? (segment.count / bar.total) * 100 : 0}%`,
                        backgroundColor: segment.color
                      }}
                      title={`${bar.label} · ${segment.label}: ${segment.count.toLocaleString()}`}
                    />
                  ) : null
                )}
              </div>
            </div>
            <span className="tw-w-8 tw-shrink-0 tw-text-right tw-font-semibold tw-tabular-nums tw-text-ink">
              {bar.total.toLocaleString()}
            </span>
          </div>
        ))}
        <ul className="tw-m-0 tw-mt-1 tw-flex tw-list-none tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-p-0">
          {legend.map((segment) => (
            <li key={segment.label} className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
              <span
                className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-sm"
                style={{ backgroundColor: segment.color }}
                aria-hidden="true"
              />
              {segment.label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <ParentWidget<PcrPieChartData>
      id={id}
      title={title}
      size={size}
      data={data}
      redirect={redirect}
      metadata={metadata}
      onRefresh={onRefresh}
      requiredPermission={requiredPermission}
      isEmpty={isEmpty}
      headerExtra={headerExtra}
    >
      {renderBars}
    </ParentWidget>
  );
}

export default PcrStackedBarWidget;
